"use client";

import { USER_PROFILE_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { useGlobalStore } from "@/store/global-store";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";

type TFollowProfileButtonProps = {
	isProfileOwner?: boolean;
	isProfileVerified?: boolean;
	username?: string;
	isFollowing?: boolean;
	className?: string;
};

const FollowProfileButton = ({
	isProfileVerified,
	username = "",
	isFollowing = false,
	isProfileOwner = false,
	className,
}: TFollowProfileButtonProps) => {
	const queryClient = useQueryClient();
	const { isLoggedIn, user } = useAuth();
	const { openModal } = useModal();
	const isVerifiedUser = user?.isVerified || false;

	// TODO: Global store will have the following profiles information of the currently logged in user
	const { followingProfiles, addFollowingProfile, removeFollowingProfile } = useGlobalStore();
	const isFollowingFromGlobalStore = isFollowing || followingProfiles.includes(username);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleFollow = async (type: string, username: string) => {
		try {
			setIsLoading(true);
			const FOLLOW_API_URL = `${USER_PROFILE_API}/${username}/follow`;
			const shouldFollow = type === "follow";

			const response = await fetch(FOLLOW_API_URL, {
				method: shouldFollow ? "PUT" : "DELETE",
			});
			const { message } = await response.json();

			if (response.ok) {
				if (shouldFollow) {
					addFollowingProfile(username);
				} else {
					removeFollowingProfile(username);
				}

				toast.success(message);
				queryClient.invalidateQueries({ queryKey: ["profileDetails"] });
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOnClick = () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		if (!isProfileVerified) {
			toast.error("Account you are trying to follow is not verified");
			return;
		}

		if (!isVerifiedUser) {
			toast.error("Please verify your account to follow others");
			return;
		}

		if (!isProfileOwner) {
			handleFollow(isFollowingFromGlobalStore ? "unfollow" : "follow", username);
			return;
		}
	};

	return (
		<button
			type="button"
			className={cn(
				"block w-full whitespace-nowrap rounded-md bg-brand-primary px-4 py-2 text-center font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 md:w-fit md:px-6",
				className
			)}
			onClick={handleOnClick}
			disabled={isLoading || isProfileOwner || !isProfileVerified}
			aria-disabled={isLoading}
		>
			{isLoading ? "..." : isFollowingFromGlobalStore ? "Un-Follow" : "Follow"}
		</button>
	);
};

export default FollowProfileButton;
